import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Avatar,
  Box,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Card, Button, Chip
} from '@material-ui/core';
import getInitials from '../../utils/getInitials';
import MainBookEditor from './MainBookEditor';
import OrderedTableHead from './OrderedTableHead';
import TableNavigation from '../general/TableNavigation';
import { useNavigate } from 'react-router-dom';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import { deleteTagFromBook } from '../../services/api';
import TagEditor from './TagEditor';
import CancelIcon from '@mui/icons-material/Cancel';

const BookListResults = ({ bookStore, userStore, hidePagination = false, hideMainBookColumn = false, hideListColumn = false, showDefaultColumn = false, onSetDefault, ...rest }) => {
  const navigate = useNavigate();
  const [loadingBooks, setLoadingBooks] = useState(false);
  const [settingDefault, setSettingDefault] = useState(null);

  const handleChipDelete = async (book, tag) => {
    await bookStore.removeTag(book, tag);
  }

  const date = (date) => {
    if (!date) {
      return '';
    }

    const dt = new Date(date);
    dt.setHours(dt.getHours() - 3);
    return dt.getDate() + '/' + `${ (dt.getMonth() + 1) }`.padStart(2, '0') + '/' + dt.getFullYear();
  };

  return (
    <Card>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <OrderedTableHead
                bookStore={ bookStore }
                label='ID'
                fieldId='book_id'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <OrderedTableHead
                bookStore={ bookStore }
                label='Autor'
                fieldId='authors'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <OrderedTableHead
                bookStore={ bookStore }
                label='Título'
                fieldId='title'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <TableCell>ISBN</TableCell>
              <OrderedTableHead
                bookStore={ bookStore }
                label='Qtd. listas lidos'
                fieldId='already_read'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <OrderedTableHead
                bookStore={ bookStore }
                label='Qtd. listas para ler'
                fieldId='want_to_read'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <OrderedTableHead
                bookStore={ bookStore }
                label='Última utilização'
                fieldId='last_quote_included'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              {!hideMainBookColumn && <TableCell>Livro-pai</TableCell>}
              <OrderedTableHead
                bookStore={ bookStore }
                label='Qtd. Trechos'
                fieldId='quotes_count'
                startLoading={ () => setLoadingBooks(true) }
                endLoading={ () => setLoadingBooks(false) }
              />
              <TableCell>Status</TableCell>
              <TableCell>Tags</TableCell>
              {!hideListColumn && <TableCell></TableCell>}
              {showDefaultColumn && <TableCell>Padrão</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            { loadingBooks ? (
              <TableRow><TableCell colSpan={ 7 }><LinearProgress /></TableCell></TableRow>) : bookStore.books.map((book) => (
              <TableRow hover key={ book.book_id }>
                <TableCell>{book.book_id}</TableCell>
                <TableCell>
                  <Box sx={ { alignItems: 'center', display: 'flex' } }>
                    <Avatar src={ book.cover } sx={ { mr: 2 } }>
                      { getInitials(book.title) }
                    </Avatar>
                    { book.authors }
                  </Box>
                </TableCell>
                <TableCell width={ 300 }>{ book.title }</TableCell>
                <TableCell width={ 110 }>{ book.isbn }</TableCell>
                <TableCell width={ 110 }>{ book.already_read}</TableCell>
                <TableCell width={ 110 }>{ book.want_to_read}</TableCell>
                <TableCell
                  width={ 120 }>{ date(book.last_quote_included) }</TableCell>
                {!hideMainBookColumn && (
                  <TableCell width={ 320 }>
                    <MainBookEditor key={book.id} book={ book } bookStore={ bookStore } userStore={ userStore } />
                  </TableCell>
                )}
                <TableCell width={ 100 }>
                    { book.quotes_count }
                </TableCell>
                <TableCell>{ book.enabled ? <CheckCircleIcon /> : <DoNotDisturbAltIcon />}</TableCell>
                <TableCell>
                  { book.book?.admin_tags?.map((tag) => (<Chip label={ tag.name } style={{backgroundColor: tag.color, color: 'white'}} onDelete={()=> handleChipDelete(book, tag)} deleteIcon={<CancelIcon style={{color: 'rgba(255, 255, 255, 0.54)'}} />} />)) }
                  <br />
                  <TagEditor book={ book } bookStore={ bookStore } userStore={ userStore } /></TableCell>
                {!hideListColumn && (
                  <TableCell>
                    <Button onClick={() => navigate(`/app/quotes/${book.book_id}`)}>Lista</Button>
                  </TableCell>
                )}
                {showDefaultColumn && (
                  <TableCell>
                    {book.is_main_default
                      ? <Chip label="Padrão" style={{ backgroundColor: '#2e7d32', color: 'white' }} />
                      : (
                        <Button
                          size="small"
                          disabled={settingDefault === book.book_id}
                          onClick={async () => {
                            setSettingDefault(book.book_id);
                            await onSetDefault?.(book);
                            setSettingDefault(null);
                          }}
                        >
                          Definir como padrão
                        </Button>
                      )
                    }
                  </TableCell>
                )}
              </TableRow>
            )) }
          </TableBody>
        </Table>
      </TableContainer>
      {!hidePagination && (
        <TableNavigation
          currentPage={ bookStore.pages.current + 1 }
          itensPerPage={ bookStore.limit }
          totalItens={ bookStore.totalBooks }
          totalPages={ bookStore.pages.total }
          onPageChange={ async (page) => {
            setLoadingBooks(true);
            await bookStore.setPage(page);
            setLoadingBooks(false);
          } }
        />
      )}
    </Card>
  );
};

BookListResults.propTypes = {
  bookStore: PropTypes.object
};

export default BookListResults;
